import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const publicRegistrationPath = '/inscricao-evento';
const localHostnames = new Set(['127.0.0.1', 'localhost', '0.0.0.0']);

function normalizeInternalTarget(target) {
  if (!target) return '';
  const trimmedTarget = String(target).trim();
  if (!/^https?:/i.test(trimmedTarget)) return trimmedTarget;

  try {
    const url = new URL(trimmedTarget);
    if (localHostnames.has(url.hostname) || url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return trimmedTarget;
  }

  return trimmedTarget;
}

export default function BannerCarousel({ banners, autoPlay = true, interval = 9000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const navigate = useNavigate();
  const currentBanner = banners?.[currentIndex];

  useEffect(() => {
    if (!autoPlay || isPaused || banners.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, interval);
    
    return () => clearInterval(timer);
  }, [autoPlay, isPaused, interval, banners.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const openBannerTarget = (target) => {
    const normalizedTarget = normalizeInternalTarget(target);
    if (!normalizedTarget) return;
    if (/^(https?:|mailto:|tel:)/i.test(normalizedTarget)) {
      window.open(normalizedTarget, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(normalizedTarget.startsWith('/') ? normalizedTarget : `/${normalizedTarget}`);
  };

  const handleSignupClick = () => {
    openBannerTarget(currentBanner?.signup_link || publicRegistrationPath);
  };

  const handleLinkClick = () => {
    openBannerTarget(currentBanner?.link);
  };

  const openBannerDetails = () => {
    const url = new URLSearchParams();
    if (currentBanner?.id) url.set('bannerId', String(currentBanner.id));
    navigate(`${createPageUrl('Avisos')}${url.toString() ? `?${url}` : ''}`);
  };

  if (!banners || banners.length === 0) {
    return (
      <div className="w-full h-48 md:h-80 lg:h-[32rem] bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
        <p className="text-white text-lg">Nenhum aviso disponível</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-48 md:h-80 lg:h-[32rem] rounded-2xl overflow-hidden group">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0"
        >
          <div className="w-full h-full relative">
            <img
              src={currentBanner.image_url}
              alt={currentBanner.title || 'Banner'}
              className="absolute inset-0 w-full h-full object-cover"
              loading={currentIndex === 0 ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={currentIndex === 0 ? 'high' : 'low'}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <button
              type="button"
              className="absolute inset-0 z-10 cursor-pointer"
              onClick={openBannerDetails}
              aria-label={`Abrir banner ${currentBanner.title || ''} em todos os avisos`}
            />
            <div className="absolute bottom-0 left-0 right-0 z-20 p-4 md:p-6 pointer-events-none">
              <h3 className="text-white text-lg md:text-xl font-bold mb-1">
                {currentBanner.title}
              </h3>
              {currentBanner.description && (
                <p className="text-white/90 text-sm md:text-base line-clamp-2">
                  {currentBanner.description}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {currentBanner.signup_enabled && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="bg-emerald-500 text-white hover:bg-emerald-600 pointer-events-auto"
                    onClick={handleSignupClick}
                  >
                    Faça sua inscrição
                  </Button>
                )}
                {currentBanner.link && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="pointer-events-auto"
                    onClick={handleLinkClick}
                  >
                    Saiba mais
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {banners.length > 1 && (
        <>
          <button
            onClick={() => setIsPaused((current) => !current)}
            className="absolute right-3 top-3 z-30 w-10 h-10 bg-black/35 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
            aria-label={isPaused ? 'Retomar banners' : 'Pausar banners'}
            title={isPaused ? 'Retomar banners' : 'Pausar banners'}
          >
            {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
          </button>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 z-30 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 z-30 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          <div className="absolute bottom-2 left-1/2 z-30 -translate-x-1/2 flex gap-1.5">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white w-6' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
